import { useEffect, type RefObject } from "react";

/** De vaste koptekst van DierMatch is 58-64px hoog en blijft staan; dit is die hoogte plus lucht. */
const KOPRUIMTE = 74;

/**
 * Elke stap van een wizard begint bovenaan.
 *
 * <p><b>Waarom dit nodig is.</b> "Volgende" staat onderaan de kaart. Zonder deze hook opent de
 * volgende stap op precies die hoogte, en kijk je naar het staartje van vragen die je nog nooit
 * gezien hebt; de stapbalk, de titel en het uitlegblok ernaast staan dan boven je beeld
 * (Maarten, 30-10-2026).
 *
 * <p><b>Waarom er twee keer gescrold wordt.</b> De pagina verandert van hoogte op het moment dat de
 * stap wisselt: een kortere stap maakt hem korter, waarop de browser je scrollpositie zelf meeduwt
 * en een lopende scroll afbreekt; een formulier dat bóven je beeld opengaat duwt je er juist
 * honderden pixels vanaf (dat is scroll-anchoring, en dat is precies wat het hoort te doen).
 * Allebei gemeten in de echte site op 30-10-2026, en allebei gebeuren ze ná de eerste scroll. De
 * tweede scroll mikt opnieuw, vanuit de uitgerekende pagina. Hij gaat naar dezelfde plek, dus wie
 * al goed zat ziet niets gebeuren.
 *
 * <p>Niet {@code scrollIntoView}, maar een eigen doel in documentcoördinaten: dat getal blijft
 * hetzelfde terwijl er gescrold wordt, dus de tweede poging corrigeert alleen wat de pagina zelf
 * heeft verschoven.
 *
 * @param doel    het element dat bovenaan moet komen
 * @param sleutel verandert zodra er een andere stap in beeld komt
 */
export function useBovenaan(doel: RefObject<HTMLElement | null>, sleutel: unknown): void {
  useEffect(() => {
    const naarBoven = () => {
      const el = doel.current;
      if (!el || typeof el.getBoundingClientRect !== "function") return;
      const rustig = globalThis.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
      const top = el.getBoundingClientRect().top + (globalThis.scrollY ?? 0) - KOPRUIMTE;
      globalThis.scrollTo?.({ top: Math.max(top, 0), behavior: rustig ? "auto" : "smooth" });
    };
    naarBoven();
    const nogEens = globalThis.setTimeout(naarBoven, 120);
    return () => globalThis.clearTimeout(nogEens);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `doel` is een ref en verandert niet.
  }, [sleutel]);
}
